import { CrumbBlock } from 'components/city-blocks/crumb';
import Head from 'next/head';
import React, { Fragment } from 'react';
import { Crumb } from 'utils/breadcrumb-util';
import { getFrontEndUrl } from 'utils/environment-util';

interface IProps {
  className?: string;
  crumbs: Crumb[];
}

// LD+JSON: Linked Data - for SEO
// see https://schema.org/
function addProductJsonLd(input: unknown) {
  return {
    __html: JSON.stringify(input),
  };
}

// For collapsing to 3 crumbs on mobile devices
const ELLIPSIS_CRUMB = (
  <div key='ellipsis' className='mr-2 block md:hidden'>
    <i className='fas fa-chevron-right mr-2 text-xs' />
    <span className='align-middle'>...</span>
  </div>
);

export const Breadcrumbs: React.FC<IProps> = ({ className, crumbs }) => {
  const ldJson = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: crumbs.map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@id': `${getFrontEndUrl({ withBasePath: true })}${crumb.href}`,
        name: crumb.name,
      },
    })),
  };

  const renderedCrumbs: React.ReactElement[] = crumbs.map((crumb, indx) => {
    const alwaysShow = indx === 0 || indx === crumbs.length - 1 || indx === crumbs.length - 2;
    const spliceEllipsis = indx === 0 && crumbs.length >= 4;
    return (
      <Fragment key={indx}>
        <CrumbBlock crumb={crumb} className={`mr-[6px] md:mr-2 ${alwaysShow ? 'block' : 'hidden md:block'}`} />
        {spliceEllipsis && ELLIPSIS_CRUMB}
      </Fragment>
    );
  });

  return (
    <>
      <Head>
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={addProductJsonLd(ldJson)}
          key='Breadcrumbs LD+JSON'></script>
      </Head>
      <div
        className={`-ml-2 flex w-fit flex-row items-center px-2 text-sm font-semibold text-neutral-200 ${className}`}>
        {renderedCrumbs}
      </div>
    </>
  );
};
